// Turning an approved template plus one recipient into the message that recipient actually gets.
//
// This mirrors `buildBodyComponent` in backend/src/services/cequensTemplateService.js, and the
// mirroring is the point: a preview that resolves variables by its own rules is worse than no
// preview, because it tells the operator a message is fine when the send will produce something
// else. Three rules are copied deliberately:
//
//  1. The loop is driven by the indices in the BODY, never by the keys of `variableMapping`. Doing
//     it the other way round is a positional bug the backend already had and fixed — a mapping with
//     a stale extra key would shift every parameter after it.
//  2. A mapped field that is blank on the contact sends an EMPTY STRING, not a fallback and not the
//     placeholder. WhatsApp then rejects that one recipient, so it is worth showing as its own
//     state rather than hiding.
//  3. An index with no contact field behind it is not a degraded send but a guaranteed total
//     failure — parameter count mismatch, every message rejected — which is why the server and the
//     Launch button both refuse it outright.

import { extractTemplateVariables, MappingReview } from './templateVariables';
import { templateValueFor } from './contactFields';

/**
 * What a template variable can be filled from. Mirrors MAPPABLE_CONTACT_FIELDS, except that the
 * two derived fields there (`currentCountry` as a name, `budgetWithCurrency`) are built from the
 * stored `currentCountry` code and `budgetCurrency` here, by templateValueFor.
 */
export interface PreviewRecipient {
  id: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  propertyInterest?: string | null;
  budget?: string | null;
  budgetCurrency?: string | null;
  locationPreference?: string | null;
  currentCountry?: string | null;
  notes?: string | null;
  source?: string | null;
}

export interface PreviewTemplate {
  id?: string;
  name?: string;
  language?: string;
  body?: string | null;
  footer?: string | null;
  headerType?: string | null;
  headerContent?: string | null;
  buttons?: TemplateButton[] | null;
  variableMapping?: Record<string, string> | null;
  metadata?: {
    variableLabels?: Record<string, string> | null;
    mappingReview?: MappingReview | null;
  } | null;
}

export interface TemplateButton {
  type: string;
  text: string;
  url?: string | null;
  phoneNumber?: string | null;
}

/**
 * `sendRequirements` from GET /whatsapp-templates/:id — what a template needs supplied before it can
 * be sent, derived on the server from the provider's own component list.
 *
 * It is the only source that knows whether a header's text or a button's URL holds a {{n}}, because
 * the flat columns strip that away. And it matters beyond convenience: a message is accepted only
 * when the components we send match the ones the template was APPROVED with, so a missing image
 * header is a REJECTED message, not a plainer one.
 *
 * Lives here rather than beside each consumer. It was previously declared three times over — here,
 * in pages/WhatsAppTemplates.tsx and in components/CampaignLaunchEditor.tsx — which is the same
 * duplication `templateVariables.ts` documents the cost of.
 */
export interface SendRequirements {
  header: { format: string; text: string | null; hasVariable: boolean; sampleMediaUrl: string | null } | null;
  bodyIndices: string[];
  urlButtons: Array<{ index: number; text: string; url: string | null; hasVariable: boolean }>;
  hasCarousel: boolean;
}

export type BodySegment =
  /** Literal template text between placeholders. */
  | { kind: 'text'; text: string }
  /** A mapped field with a value on this recipient. */
  | { kind: 'value'; text: string; index: string; field: string }
  /** Mapped, but blank on this recipient — an empty parameter, which WhatsApp rejects. */
  | { kind: 'empty'; index: string; field: string; label?: string }
  /** No recipient chosen, so there is nothing to resolve against yet. */
  | { kind: 'missing'; index: string; label?: string }
  /** No contact field mapped to this index — the send is refused. */
  | { kind: 'unmapped'; index: string; label?: string };

/** The provider's own label for a variable, when the approved template carried an example. */
const labelFor = (template: PreviewTemplate, index: string): string | undefined =>
  template.metadata?.variableLabels?.[index] ?? undefined;

/**
 * Split a body into literal text and resolved placeholders, in the order they appear.
 *
 * Splitting on the placeholder rather than replacing one index at a time is what makes `{{2}} …
 * {{1}}` render where each actually appears, and a repeated `{{1}}` render at both positions.
 */
export function resolveBodySegments(
  template: PreviewTemplate | null | undefined,
  recipient?: PreviewRecipient | null,
): BodySegment[] {
  const body = String(template?.body ?? '');
  if (!body) return [];

  const mapping = template?.variableMapping || {};
  // Only the placeholders the send will actually fill — numbered, or named when there are none —
  // so a stray {{word}} in a numbered body stays literal here exactly as it does on the wire.
  const variables = new Set(extractTemplateVariables(body));

  return body
    .split(/(\{\{[A-Za-z0-9_]+\}\})/g)
    .filter((part) => part !== '')
    .map<BodySegment>((part) => {
      const match = part.match(/^\{\{([A-Za-z0-9_]+)\}\}$/);
      if (!match || !variables.has(match[1])) return { kind: 'text', text: part };

      const index = match[1];
      const label = labelFor(template!, index);
      const field = mapping[index];

      if (!field) return { kind: 'unmapped', index, label };
      if (!recipient) return { kind: 'missing', index, label };

      // Through templateValueFor, the browser twin of the backend's templateValuesFor, so a
      // country reads as its name in the template's language — exactly what the customer gets.
      const text = templateValueFor(recipient as unknown as Record<string, unknown>, field, template?.language);

      return text.trim()
        ? { kind: 'value', text, index, field }
        : { kind: 'empty', index, field, label };
    });
}

/**
 * The same substitution for a header's text or a button's URL.
 *
 * These take ONE value for the whole campaign rather than a per-recipient one — the launch payload
 * carries a single `headerText` and a single value per button index — so they do not resolve
 * against a contact and are not part of `resolveBodySegments`.
 */
export function fillTemplateValue(text: string | null | undefined, value?: string | null): {
  filled: string;
  hasBlank: boolean;
} {
  const source = String(text ?? '');
  if (!source) return { filled: '', hasBlank: false };

  let hasBlank = false;
  const filled = source.replace(/\{\{\d+\}\}/g, () => {
    if (value && value.trim()) return value;
    hasBlank = true;
    return '';
  });

  return { filled, hasBlank };
}

/** Whether a template can be previewed truthfully at all. */
export function isPreviewable(requirements?: SendRequirements | null): boolean {
  return !requirements?.hasCarousel;
}

export interface PreviewButton {
  type: string;
  text: string;
  /** The URL as it would be sent, with any {{n}} filled from buttonValues. */
  url: string | null;
  /** True when this button's URL holds a {{n}} that has not been given a value. */
  needsValue: boolean;
}

/**
 * The buttons as WhatsApp would render them.
 *
 * Drawn from the flat `buttons` column, which is the only place a QUICK_REPLY survives — the
 * provider's component list is flattened at sync and `sendRequirements` reports only URL buttons.
 * The two are joined by INDEX, and that join is exact because `urlButtons[].index` is the position
 * in the full button list, not in the URL-only subset.
 */
export function resolvePreviewButtons(
  template: PreviewTemplate | null | undefined,
  requirements?: SendRequirements | null,
  buttonValues?: Record<string, string> | null,
): PreviewButton[] {
  const buttons = template?.buttons || [];
  const dynamic = new Map(
    (requirements?.urlButtons || []).filter((b) => b.hasVariable).map((b) => [b.index, b]),
  );

  return buttons.map((button, index) => {
    const isDynamic = dynamic.has(index);

    if (!isDynamic) {
      // A URL button with no {{n}} sends no component at all: the approved URL goes out as-is.
      return { type: button.type, text: button.text, url: button.url ?? null, needsValue: false };
    }

    const { filled, hasBlank } = fillTemplateValue(button.url, buttonValues?.[String(index)]);
    return { type: button.type, text: button.text, url: filled || null, needsValue: hasBlank };
  });
}

/** Indices a body uses, for the legend under a placeholder-only preview. */
export function previewVariableLegend(
  template: PreviewTemplate | null | undefined,
): Array<{ index: string; field?: string; label?: string }> {
  if (!template) return [];
  const mapping = template.variableMapping || {};
  return extractTemplateVariables(template.body).map((index) => ({
    index,
    field: mapping[index],
    label: labelFor(template, index),
  }));
}
